import { i18n, InjectorHelper, MetadataConstants, MetadataSelectors, SelectHelper, SessionSelectors } from 'invision-core';
import { convertStringToNumber } from 'invision-core/src/components/helpers/conversion.helper';
import { CODES } from 'invision-core/src/components/metadata/codes/codes.constants';
import { MetadataCodeTypeDictionarySelector } from 'invision-core/src/components/metadata/codes/codes.selectors';
import { createImmutableSelector } from 'invision-core/src/utilities/create.immutable.selector';
import moment from 'moment';
import { isEmpty, isNil, partial, pathOr, values } from 'ramda';
import { createSelector } from 'reselect';
import { SUBSCRIPTION_DETAILS_ROUTE } from '../../components/customer/subscriptions/subscriptions.config';
import LocaleKeys from './../../locales/keys';
import { PaymentInstrumentsSelector } from './../../reducers/selectors/customer.ewallet.selectors';
import { RouteParams, RoutePreviousState, SelectedCustomerSelector } from './customer.selectors';
import { IsTransactionReceiptTaxBreakoutEnabledSelector } from './search.selectors';
import { getFormattedServiceAttributeValue } from './services.list.selectors.helper';
var EMPTY_ARRAY = [];
var RecoverableTransactionsSelector = function RecoverableTransactionsSelector(state) {
  return state.customercare.recoverableUiState.transactions;
};
export var PageNumberSelector = createSelector([RecoverableTransactionsSelector], function (uiState) {
  return uiState.pageNumber ? uiState.pageNumber : 0;
});
var TransactionFilterData = createSelector([RecoverableTransactionsSelector], function (recoverableInteraction) {
  return recoverableInteraction.filterData;
});
export var HasSelectedTransactionFilters = createSelector([TransactionFilterData], function (transactionFilters) {
  return values(transactionFilters).some(function (item) {
    return !(isNil(item) || isEmpty(item));
  });
});
export var PaymentInstrumentIdUrlSearchParamSelector = createSelector([RouteParams], function (routeParams) {
  return routeParams.paymentInstrumentId;
});
export var BrandableCurrencySelector = createSelector([TransactionFilterData], function (filterData) {
  return filterData.brandableCurrency;
});
export var CurrencySelector = createSelector([TransactionFilterData], function (filterData) {
  return filterData.currency;
});
export var EndDateSelector = createSelector([TransactionFilterData], function (filterData) {
  return filterData.endDate;
});
export var LockerItemIdSelector = createSelector([TransactionFilterData], function (filterData) {
  return filterData.lockerItemId;
});
export var PaymentInstrumentSelector = createSelector([TransactionFilterData], function (filterData) {
  return filterData.paymentInstrument;
});
export var PreviousRouteSelector = createSelector([RouteParams], function (params) {
  return params.previousRoute;
});
export var StartDateSelector = createSelector([TransactionFilterData], function (filterData) {
  return filterData.startDate;
});
export var TransactionResultSelector = createSelector([TransactionFilterData], function (filterData) {
  return filterData.transactionResult;
});
export var TransactionTypesSelector = createImmutableSelector([TransactionFilterData], function (filterData) {
  return filterData.transactionTypes;
});
export var IsFetchingDataSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.transactions.isFetchingData;
});
export var TransactionErrorSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.transactions.transactionsError;
});
export var RecordCountSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.transactions.recordCount ? selectedCustomer.transactions.recordCount : 0;
});
export var EndRecordSelector = createSelector([PageNumberSelector, SessionSelectors.PageSizePreferenceSelector, RecordCountSelector], function (pageNumber, pageSize, recordCount) {
  var endNumber = pageNumber * pageSize;
  if (endNumber < recordCount) {
    return endNumber;
  }
  return recordCount;
});
export var StartRecordSelector = createSelector([PageNumberSelector, SessionSelectors.PageSizePreferenceSelector, RecordCountSelector], function (pageNumber, pageSize, recordCount) {
  if (recordCount <= 0) {
    return 0;
  }
  return (pageNumber - 1) * pageSize + 1;
});
export var TransactionDataSelector = createImmutableSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.transactions.data;
});
export var IsEligibleForPartialRefundsSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.transactions.AllowPartialRefunds;
});
export var ProductsDataSelector = createImmutableSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.transactions.Products;
});
export var PricingPlansDataSelector = createImmutableSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.transactions.PricingPlans;
});
export var OfferingsDataSelector = createImmutableSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.transactions.Offerings;
});
export var CanRetryTransactionSelector = createImmutableSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.transactions.canRetryTransaction || false;
});
export var CurrentTransactionIdSelector = createSelector([RouteParams], function (routeParams) {
  return routeParams.transactionId;
});
export var CurrentTransactionFromSearchSelector = createSelector([RouteParams], function (routeParams) {
  return routeParams.fromTransactionSearch || false;
});
var getFormattedTransactionDetails = function getFormattedTransactionDetails(details) {
  if (details && details.PaymentAmounts) {
    return details.set('paymentAmounts', details.PaymentAmounts.map(function (item) {
      if (details.BrandableCurrency) {
        return item.setIn(['PaymentInstrument', 'StoredValueAccount', 'BrandableCurrencyName'], details.BrandableCurrencyName);
      } else {
        return item;
      }
    })).set('paymentInstruments', details.PaymentAmounts.map(function (_ref) {
      var PaymentInstrument = _ref.PaymentInstrument;
      return PaymentInstrument;
    }));
  }
  return details;
};
export var CurrentTransactionDetailsSelector = createSelector([CurrentTransactionIdSelector, TransactionDataSelector], function (transactionId, transactions) {
  return transactionId && transactions && transactions[transactionId] ? getFormattedTransactionDetails(transactions[transactionId].details) : null;
});

/// Changed by ASC-95425 to force header label change for more generic
var changeTransactionChargeName = function changeTransactionChargeName(Type, TypeName) {
  switch (Type) {
    case 31:
      return i18n.translate(LocaleKeys.TRANSACTION_TYPE.PAYMENT);
    default:
      return TypeName;
  }
};
export var shouldLinkPurchaseOrderItemTypeRow = function shouldLinkPurchaseOrderItemTypeRow(_ref2) {
  var referenceTransactionId = _ref2.referenceTransactionId,
    isConsolidatedCosPaymentHandlingEnabled = _ref2.isConsolidatedCosPaymentHandlingEnabled;
  return !!isConsolidatedCosPaymentHandlingEnabled && !!referenceTransactionId;
};
export var getPurchaseOrderItemTypeTooltip = function getPurchaseOrderItemTypeTooltip(_ref3) {
  var referenceTransactionId = _ref3.referenceTransactionId,
    isAppliedItem = _ref3.isAppliedItem,
    isConsolidatedCosPaymentHandlingEnabled = _ref3.isConsolidatedCosPaymentHandlingEnabled;
  if (!shouldLinkPurchaseOrderItemTypeRow({
    referenceTransactionId: referenceTransactionId,
    isConsolidatedCosPaymentHandlingEnabled: isConsolidatedCosPaymentHandlingEnabled
  })) {
    return undefined;
  }

  // Side detection is per credit row (root vs AppliedItems), not transaction IsConsolidated —
  // required for chained COS where one transaction can be both consolidated and originating.
  // AppliedItems credit → link/tooltip toward the consolidated COS where the credit was applied.
  // Root credit → link/tooltip toward the original purchase transaction.
  return i18n.translate(isAppliedItem ? LocaleKeys.TRANSACTION_DETAILS.CONSOLIDATED_TRANSACTION_TOOLTIP : LocaleKeys.TRANSACTION_DETAILS.ORIGINATING_TRANSACTION_TOOLTIP, {
    transactionId: referenceTransactionId
  });
};
var getMappedPurchaseOrderItems = function getMappedPurchaseOrderItems(_ref4, products, pricingPlans, offerings, regularExpressionCodeTable, serviceAttributeCodeTable) {
  var Currency = _ref4.Currency,
    BrandableCurrencyName = _ref4.BrandableCurrencyName,
    Items = _ref4.Items;
  return (Items || []).map(function (item) {
    return {
      label: changeTransactionChargeName(item.Type, item.TypeName),
      id: item.Id,
      attemptedTotal: item.AttemptedTotalAmount,
      transactedTotal: item.TransactedTotalAmount,
      currency: Currency,
      brandableCurrencyName: BrandableCurrencyName,
      creditableAmount: item.CreditableAmount,
      formattedServiceIdentifier: item.PurchaseInformation && item.PurchaseInformation.ServiceIdentifier ? getFormattedServiceAttributeValue(item.PurchaseInformation.ServiceIdentifier.ServiceAttributeId, item.PurchaseInformation.ServiceIdentifier.Value, serviceAttributeCodeTable, regularExpressionCodeTable) : undefined,
      offerId: item.PurchaseInformation ? item.PurchaseInformation.OfferingId : undefined,
      offerName: offerings && item.PurchaseInformation && item.PurchaseInformation.OfferingId && offerings[item.PurchaseInformation.OfferingId] ? offerings[item.PurchaseInformation.OfferingId].Name : undefined,
      pricingPlanId: item.PurchaseInformation ? item.PurchaseInformation.PricingPlanId : undefined,
      pricingPlanName: item.PurchaseInformation && item.PurchaseInformation.PricingPlanId && pricingPlans[item.PurchaseInformation.PricingPlanId] ? pricingPlans[item.PurchaseInformation.PricingPlanId].Name : undefined,
      productId: item.PurchaseInformation ? item.PurchaseInformation.ProductId : undefined,
      productName: item.PurchaseInformation && item.PurchaseInformation.ProductId && products[item.PurchaseInformation.ProductId] ? products[item.PurchaseInformation.ProductId].Name : undefined,
      purchaseInformation: item.PurchaseInformation,
      tableData: [{
        description: item.Description,
        attempted: item.AttemptedAmount,
        transacted: item.TransactedAmount,
        type: changeTransactionChargeName(item.Type, item.TypeName),
        referenceTransactionId: item.ReferenceTransactionId,
        isAppliedItem: false,
        date: item.Created,
        currency: Currency,
        brandableCurrency: BrandableCurrencyName
      }].concat((item.AppliedItems || []).map(function (applied) {
        return {
          description: applied.Description,
          attempted: applied.AttemptedAmount,
          transacted: applied.TransactedAmount,
          type: changeTransactionChargeName(applied.Type, applied.TypeName),
          referenceTransactionId: applied.ReferenceTransactionId,
          isAppliedItem: true,
          date: applied.Created,
          currency: Currency,
          brandableCurrency: BrandableCurrencyName,
          externalReference: applied.ExternalReference
        };
      }))
    };
  });
};
export var CurrentTransactionDetailsPurchaseOrderItemsSelector = createSelector([CurrentTransactionDetailsSelector, ProductsDataSelector, PricingPlansDataSelector, OfferingsDataSelector, MetadataCodeTypeDictionarySelector(CODES.RegularExpression), MetadataCodeTypeDictionarySelector(CODES.ServiceAttribute)], function (transactionDetails, products, pricingPlans, offerings, regularExpressionCodeTable, serviceAttributeCodeTable) {
  return transactionDetails && transactionDetails.PurchaseOrder ? getMappedPurchaseOrderItems(transactionDetails.PurchaseOrder, products, pricingPlans, offerings, regularExpressionCodeTable, serviceAttributeCodeTable) : EMPTY_ARRAY;
});
export var MutableTransactionDetailPaymentInstruments = createSelector([CurrentTransactionDetailsSelector], function (transactionDetails) {
  return transactionDetails && transactionDetails.paymentInstruments ? transactionDetails.paymentInstruments.asMutable({
    deep: true
  }) : EMPTY_ARRAY;
});
export var TransactionsSelector = createSelector([TransactionDataSelector], function (transactionData) {
  return values(transactionData).sort(function (a, b) {
    return moment(a.Created).isBefore(moment(b.Created)) ? 1 : -1;
  });
});
export var TransactionDetailsSelector = createSelector([TransactionDataSelector, CurrentTransactionIdSelector], function (transactions, currentTransactionId) {
  return transactions[currentTransactionId];
});
var toCurrencyForPropertyOnTotals = function toCurrencyForPropertyOnTotals(details, prop) {
  var $filter = InjectorHelper.getFilterService();
  var amount = details.BrandableCurrencyName ? "".concat(details.PurchaseOrder.Totals[prop], " ").concat(details.BrandableCurrencyName) : details.PurchaseOrder.Currency ? $filter('invCurrency')(details.PurchaseOrder.Totals[prop], details.PurchaseOrder.Currency, undefined, undefined, true) : "".concat(details.PurchaseOrder.Totals[prop]);
  return numberFormat(details.PurchaseOrder.Totals[prop], amount);
};
var mapPurchaseOrderItems = partial(function (Currency, BrandableCurrencyName, $filter, _ref5) {
  var Created = _ref5.Created,
    Description = _ref5.Description,
    TransactedTotalAmount = _ref5.TransactedTotalAmount,
    TypeName = _ref5.TypeName;
  return {
    Created: $filter('date')(Created, 'short'),
    Description: Description,
    TransactedTotalAmount: numberFormat(TransactedTotalAmount, BrandableCurrencyName ? "".concat(TransactedTotalAmount, " ").concat(BrandableCurrencyName) : Currency ? $filter('invCurrency')(TransactedTotalAmount, Currency || '', undefined, true, true) : "".concat(TransactedTotalAmount)),
    TypeName: TypeName
  };
});
var numberFormat = function numberFormat(flatAmount, amount) {
  // eslint-disable-next-line no-useless-escape
  var symbol = amount.replace(/[\d\-\.\s]/gmi, '');
  return flatAmount < 0 ? "".concat(symbol).concat(flatAmount) : amount;
};

// https://qa-documentation.ascendon.tv/v17.4/data.aspx?type=ContentDirect.CoreService.Contract.Data.SubscriberManagement.PurchaseOrderItemType&assembly=ContentDirect.CoreService.Contract
// Taxes are hidden from the receipt (as they are displayed as a sum value)
var CREDIT_TYPES = {
  COUPON_DISCOUNT: 24,
  CREDIT: 2,
  DISCOUNT: 8,
  DISCRETIONARY_DISCOUNT: 21,
  EPISODIC_PURCHASE_DISCOUNT: 30,
  GLOBAL_DISCOUNT: 25,
  INSTANT_DISCOUNT: 26,
  PRICING_PLAN_DISCOUNT: 22,
  PRODUCT_PURCHASE_DISCOUNT: 23,
  QUANTITY_DISCOUNT: 29,
  REFUND: 3,
  SHIPPING_AND_HANDLING_DISCOUNT: 27,
  WRITE_OFF: 20
};
var PURCHASE_ITEMS = {
  A_LA_CARTE_PURCHASE: 31,
  BALANCE_ACCOUNT_CHARGE: 15,
  BALANCE_ACCOUNT_PURCHASE: 14,
  GIFT_CARD_PURCHASE: 13,
  LATE_FEE: 17,
  ONE_TIME_INSTANT_PURCHASE: 1,
  ONE_TIME_RENEWAL_PURCHASE: 9,
  PRORATED_PURCHASE: 18,
  SHIPPING_AND_HANDLING: 16,
  SUBSCRIPTION_INSTANT_PURCHASE: 11,
  SUBSCRIPTION_RENEWAL_PURCHASE: 12,
  TERMINATION_FEE: 10
};
var TAX_TYPES = {
  COUNTY_TAX: 6,
  FEDERAL_TAX: 4,
  LOCAL_TAX: 7,
  STATE_TAX: 5
};
var CREDIT_TYPE_VALUES = values(CREDIT_TYPES);
var PURCHASE_ITEMS_VALUES = values(PURCHASE_ITEMS);
var TAX_TYPES_VALUES = values(TAX_TYPES);
export var PrintTransactionDetailsSelector = createSelector([TransactionDataSelector, CurrentTransactionIdSelector, IsTransactionReceiptTaxBreakoutEnabledSelector], function (transaction, id, isTaxBreakOutEnabled) {
  if (transaction && transaction[id] && transaction[id].details && transaction[id].ReceiptContext) {
    var _transaction$id = transaction[id],
      details = _transaction$id.details,
      ReceiptContext = _transaction$id.ReceiptContext;
    var $filter = InjectorHelper.getFilterService();
    var mapPurchaseOrderItemsWithCurrency = mapPurchaseOrderItems([details.Currency, details.BrandableCurrencyName, $filter]);
    var flattenedPurchasedOrderItems = details.PurchaseOrder && details.PurchaseOrder.Items ? details.PurchaseOrder.Items.reduce(function (prev, _ref6) {
      var AppliedItems = _ref6.AppliedItems,
        Created = _ref6.Created,
        Description = _ref6.Description,
        TransactedAmount = _ref6.TransactedAmount,
        Type = _ref6.Type,
        TypeName = _ref6.TypeName;
      var toAdd = [];
      var appliedItems = AppliedItems || [];
      if (PURCHASE_ITEMS_VALUES.includes(Type)) {
        toAdd.push({
          Created: Created,
          Description: Description,
          isTopLevel: true,
          // Each item has a "top level" item, and applied items that need to be flattened
          TransactedTotalAmount: TransactedAmount,
          TypeName: TypeName
        });
      }
      if (isTaxBreakOutEnabled) {
        appliedItems = appliedItems.map(function (item) {
          return {
            Created: item.Created,
            Description: item.Description,
            TransactedTotalAmount: item.TransactedAmount !== undefined ? convertStringToNumber(item.TransactedAmount.toFixed(2)) : item.TransactedAmount,
            Type: item.Type,
            TypeName: item.TypeName,
            isCreditTax: item.TransactedAmount < 0
          };
        });
      }
      toAdd = toAdd.concat(appliedItems);
      return prev.concat(toAdd);
    }, []) : [];
    return {
      AuthorizationCode: details.ResultCode,
      BusinessUnit: ReceiptContext.BusinessUnitName,
      BusinessUnitAddress: ReceiptContext.BusinessUnitAddress,
      Items: flattenedPurchasedOrderItems.filter(function (_ref7) {
        var isTopLevel = _ref7.isTopLevel,
          Type = _ref7.Type,
          isCreditTax = _ref7.isCreditTax;
        return isTopLevel || PURCHASE_ITEMS_VALUES.includes(Type) || isTaxBreakOutEnabled && TAX_TYPES_VALUES.includes(Type) && !isCreditTax;
      }).map(mapPurchaseOrderItemsWithCurrency),
      CreditItems: flattenedPurchasedOrderItems.filter(function (_ref8) {
        var Type = _ref8.Type,
          isCreditTax = _ref8.isCreditTax;
        return CREDIT_TYPE_VALUES.includes(Type) || isCreditTax;
      }).map(mapPurchaseOrderItemsWithCurrency),
      Email: ReceiptContext.Subscriber.Email,
      Name: {
        First: ReceiptContext.Subscriber.FirstName,
        Last: ReceiptContext.Subscriber.LastName,
        CompanyName: ReceiptContext.Subscriber.CompanyName
      },
      OrderNumber: details.PurchaseOrder ? details.PurchaseOrder.OrderNumber : null,
      SalesChannelName: details.SalesChannelName,
      StoreName: details.StoreName,
      PaymentAmounts: details.PaymentAmounts ? details.PaymentAmounts.map(function (_ref9) {
        var PaymentInstrument = _ref9.PaymentInstrument;
        return Object.assign({
          name: ''
        }, PaymentInstrument);
      }) : [],
      Totals: details.PurchaseOrder ? {
        DiscountAmount: toCurrencyForPropertyOnTotals(details, 'DiscountAmount'),
        OriginalTotalAmount: toCurrencyForPropertyOnTotals(details, 'OriginalTotalAmount'),
        ReturnTaxAmount: toCurrencyForPropertyOnTotals(details, 'ReturnTaxAmount'),
        SubTotalAmount: toCurrencyForPropertyOnTotals(details, 'SubTotalAmount'),
        TaxAmount: toCurrencyForPropertyOnTotals(details, 'TaxAmount'),
        TotalAmount: toCurrencyForPropertyOnTotals(details, 'TotalAmount'),
        TotalReturnAmount: toCurrencyForPropertyOnTotals(details, 'TotalReturnAmount')
      } : null,
      TransactionDate: ReceiptContext.TransactionDate,
      TransactionId: details.Id
    };
  } else {
    return null;
  }
});
export var HasLoadedTransactions = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.transactions.hasLoadedTransactions;
});
export var TableDataSelector = createSelector([HasLoadedTransactions, TransactionsSelector], function (hasLoadedTransactions, transactions) {
  if (hasLoadedTransactions) {
    var $filter = InjectorHelper.getFilterService();
    return transactions.map(function (transaction) {
      var dateEval = transaction.Created;
      var amountEval = transaction.OriginalAmount;
      var purchasedProducts = transaction.PurchasedProducts;
      return transaction.set('Created', dateEval ? $filter('localShort')(dateEval) : null).set('OriginalAmount', amountEval && transaction.Currency ? $filter('invCurrency')(amountEval, transaction.Currency) : null).set('PurchasedProducts', purchasedProducts ? purchasedProducts : i18n.translate(LocaleKeys.NOT_APPLICABLE));
    });
  }
  return EMPTY_ARRAY;
});
export var TotalPagesSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.transactions.pageCount;
});
export var SelectedPageSizePreference = createSelector([RecoverableTransactionsSelector], function (uiState) {
  return uiState.filterData.pageSizePreference;
});
var BrandableCurrencyCodes = function BrandableCurrencyCodes(state) {
  return MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.BrandableCurrency, state);
};
export var BrandableCurrencyCodeOptionsSelector = createSelector([BrandableCurrencyCodes, BrandableCurrencySelector], function (brandableCurrencyCodes, selectedBrandableCurrency) {
  var formattedCodes = [];
  brandableCurrencyCodes.forEach(function (code) {
    var formattedCode = SelectHelper.codeValueToSelectOption(code);
    var selectedValue = selectedBrandableCurrency ? selectedBrandableCurrency.value : null;
    formattedCode.selected = formattedCode.value === selectedValue;
    formattedCodes.push(formattedCode);
  });
  return formattedCodes;
});
var CurrencyCodes = function CurrencyCodes(state) {
  return MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.Currency, state);
};
export var CurrencyCodeOptionsSelector = createSelector([CurrencyCodes, CurrencySelector], function (currencyCodes, selectedCurrency) {
  var formattedCodes = [];
  currencyCodes.forEach(function (code) {
    var formattedCode = SelectHelper.codeValueToSelectOption(code);
    var selectedValue = selectedCurrency ? selectedCurrency.value : null;
    formattedCode.selected = formattedCode.value === selectedValue;
    formattedCodes.push(formattedCode);
  });
  return formattedCodes;
});
export var TransactionFilterPaymentInstrumentSelector = createSelector([PaymentInstrumentsSelector], function (paymentInstruments) {
  return paymentInstruments.map(function (paymentInstrument) {
    if (paymentInstrument.StripeAccount) {
      return paymentInstrument.set('Name', "".concat(paymentInstrument.TypeName, " ").concat(paymentInstrument.StripeAccount.PaymentMethodType ? " - ".concat(paymentInstrument.StripeAccount.PaymentMethodType) : '', " ").concat(paymentInstrument.StripeAccount.PaymentMethodSubType ? " - ".concat(paymentInstrument.StripeAccount.PaymentMethodSubType) : ''));
    }
    return paymentInstrument.set('Name', "".concat(paymentInstrument.TypeName, ": ").concat(paymentInstrument.Name));
  });
});
export var PaymentInstrumentOptionsSelector = createSelector([TransactionFilterPaymentInstrumentSelector, PaymentInstrumentSelector], function (paymentInstruments, selectedPaymentInstrument) {
  var formattedEntities = [];
  paymentInstruments.forEach(function (entity) {
    var formattedEntity = SelectHelper.entityValueToSelectOption(entity);
    var selectedValue = selectedPaymentInstrument ? selectedPaymentInstrument.value : null;
    formattedEntity.selected = formattedEntity.value === selectedValue;
    formattedEntities.push(formattedEntity);
  });
  return formattedEntities;
});
var IsPreviousSubscriptionRouteSelector = createSelector([RoutePreviousState], function (route) {
  return route.name === SUBSCRIPTION_DETAILS_ROUTE;
});
var IsPreviousTransactionDetailRouteSelector = createSelector([RoutePreviousState], function (route) {
  return route.name.match('index.customercare.customer.transactions') !== null;
});
var ShowBackBannerSelector = createSelector([RecoverableTransactionsSelector], function (uiState) {
  return uiState.showBanner;
});
export var ShowSubscriptionBackBannerSelector = createSelector([IsPreviousSubscriptionRouteSelector, IsPreviousTransactionDetailRouteSelector, ShowBackBannerSelector], function (isPreviousSubscriptionRoute, isPreviousTransactionDetailRoute, showBackBanner) {
  return showBackBanner && (isPreviousSubscriptionRoute || isPreviousTransactionDetailRoute);
});
export var SubscriptionSelector = createSelector([RouteParams], function (params) {
  return params.subscription;
});
export var SelectedPaymentIdFromRouteSelector = createSelector([RouteParams], function (params) {
  return params.selectedPaymentId;
});
export var IsSendingEmailSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return pathOr(false, ['transactions', 'isSendingEmail'], selectedCustomer);
});